import type { ReactNode } from 'react'

type ChipVariant = 'default' | 'plain' | 'off' | 'ok' | 'warning' | 'failure' | 'grey' | 'active' | 'pending' | 'type'

/**
 * `span.chip` → `chip` (process/08-COMPONENT-CATALOGUE.md — ten variants, catalogued since W122).
 * Base: font-size 10.5px → --fs-small (+0.5px delta); weight 600 → --fw-semibold; border-radius
 * 3px → --r-1; padding 2px 6px → --sp-1 / --sp-3 (0 delta both).
 *
 * **Seven of the ten were never built until W172** — `default`/`plain`/`off` were the only
 * variants this component ever had, so every status this whole app renders (workspace protection,
 * job outcome, credential health, connection state…) was funnelled into two colourless looks
 * regardless of what the prototype's own CSS actually does for that state. Found by comparing this
 * file directly against `process/08-COMPONENT-CATALOGUE.md`'s own already-correct chip rows — the
 * catalogue was never wrong, this component was never finished against it, and no pass across 172
 * ever checked variant coverage (`l1_conform.py` only checks a `data-cmp` tag's presence, not
 * whether the component behind it accepts the states its own catalogue promises).
 *
 * `ok`/`warning`/`failure`/`grey` (`.chip.solid-*`): white text, uppercase, letter-spacing .4px,
 * font-size 9px → --fs-micro (+1px delta), weight 800 → --fw-heavy. `plain` (`span.chip.plain`):
 * background --row, weight 500 → --fw-semibold (+100, an exact tie that rounds up per §33.9's own
 * rule). `off` (`span.chip.st-off`): background --row, same 9px/800/uppercase shape as the solid
 * variants. `active` (`.chip.st-active`): background `--chip-active-bg` (minted W172 — see
 * `process/11-TOKEN-MAP.md`), text `--accent-green`. `pending` (`.chip.st-pending`): background
 * `--chip-bg`, text `--chip-tx` — the same two tokens `default` already uses, at the smaller
 * uppercase state size rather than a new pair. `type` (`.chip.type`): background `--info-bg`
 * (`#eae6ff` folds into it at Δ12, within §33.9's own threshold), text `--accent-indigo`.
 * `letter-spacing` and `text-transform` are not token families (§33.9 names type/space/radius/
 * weight/leading/colour only) — left as plain Tailwind utilities, as `off` already did.
 */
export function Chip({ variant = 'default', children }: { variant?: ChipVariant; children: ReactNode }) {
  const base = 'inline-block rounded-1 whitespace-nowrap px-3 py-1'
  const stateShape = 'text-micro font-heavy uppercase tracking-wide'
  const byVariant: Record<ChipVariant, string> = {
    default: 'text-small font-semibold text-chip-tx bg-chip-bg',
    plain: 'text-small font-semibold text-tx2 bg-row',
    off: `${stateShape} text-tx2 bg-row`,
    ok: `${stateShape} text-white bg-pill-ok`,
    warning: `${stateShape} text-white bg-warn`,
    failure: `${stateShape} text-white bg-fail`,
    grey: `${stateShape} text-white bg-slate`,
    active: `${stateShape} text-accent-green bg-chip-active-bg`,
    pending: `${stateShape} text-chip-tx bg-chip-bg`,
    type: `${stateShape} text-accent-indigo bg-info-bg`,
  }
  return (
    <span data-cmp="chip" className={`${base} ${byVariant[variant]}`}>
      {children}
    </span>
  )
}
